import moment from 'moment'
import { ImportCollectionStatus, EAutoOnboardingStepStatus } from '../../../../types/migration'

export interface ImportStatusPresentation {
  label: string
  background: string
  color: string
  status: EAutoOnboardingStepStatus
}

const defaultPresentation: ImportStatusPresentation = {
  label: 'Not Imported',
  background: '#e0e0e0',
  color: '#424242',
  status: EAutoOnboardingStepStatus.NOT_STARTED,
}

const statusPresentations: Record<EAutoOnboardingStepStatus, ImportStatusPresentation> = {
  [EAutoOnboardingStepStatus.SUCCEEDED]: {
    label: 'Imported',
    background: '#a5d6a7',
    color: '#4caf50',
    status: EAutoOnboardingStepStatus.SUCCEEDED,
  },
  [EAutoOnboardingStepStatus.FAILED]: {
    label: 'Failed',
    background: '#ffcdd2',
    color: '#f44336',
    status: EAutoOnboardingStepStatus.FAILED,
  },
  [EAutoOnboardingStepStatus.IN_PROGRESS]: {
    label: 'In Progress',
    background: '#5ebcff',
    color: '#ffffff',
    status: EAutoOnboardingStepStatus.IN_PROGRESS,
  },
  [EAutoOnboardingStepStatus.CANCELLED]: {
    label: 'Cancelled',
    background: '#ffe0b2',
    color: '#e65100',
    status: EAutoOnboardingStepStatus.CANCELLED,
  },
  [EAutoOnboardingStepStatus.NOT_STARTED]: {
    label: 'Not Started',
    background: '#e0e0e0',
    color: '#424242',
    status: EAutoOnboardingStepStatus.NOT_STARTED,
  },
}

export const getImportStatusPresentation = (
  status?: EAutoOnboardingStepStatus | string,
): ImportStatusPresentation => {
  if (!status) {
    return defaultPresentation
  }
  const statusKey = status as EAutoOnboardingStepStatus
  return statusPresentations[statusKey] || defaultPresentation
}

export const formatImportTimestamp = (timestamp?: string | null): string | null => {
  if (!timestamp) return null
  const momentTs = moment(timestamp)
  if (!momentTs.isValid()) return null
  return momentTs.format('MM/DD/YYYY HH:mm')
}

export interface ImportErrorDetail {
  id?: string | null
  value?: string | null
  property?: string | null
  header?: string | null
  index?: number | null
  message?: string | null
}

export interface ImportErrorInfo {
  message: string | null
  details: ImportErrorDetail[]
  rawCause?: string
}

const normalizeCauseString = (rawCause: string | undefined): any => {
  if (!rawCause) return null
  try {
    return JSON.parse(rawCause)
  } catch (_error) {
    return rawCause
  }
}

const extractDetailsArray = (value: unknown): ImportErrorDetail[] => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value
      .filter((item) => typeof item === 'object' && item !== null)
      .map((item) => item as ImportErrorDetail)
  }
  return []
}

export const parseImportError = (error: unknown): ImportErrorInfo => {
  if (!error) {
    return { message: null, details: [] }
  }

  if (typeof error === 'string') {
    return { message: error, details: [], rawCause: error }
  }

  if (typeof error === 'object') {
    console.log('error object:', error)
    const errorObject = error as Record<string, unknown>
    if (errorObject?.message) {
      return { message: errorObject?.message as string, details: [] }
    }
    const directMessage =
      (errorObject.message as string | undefined) ||
      (errorObject.errorMessage as string | undefined) ||
      (errorObject.Error as string | undefined)

    const detailObject = errorObject.detail as Record<string, unknown> | undefined
    if (detailObject) {
      if (typeof detailObject.message === 'string') {
        return {
          message: detailObject.message as string,
          details: [],
          rawCause: detailObject.cause as string | undefined,
        }
      }

      const detailCauseRaw = detailObject.cause as string | undefined
      const detailCauseValue = normalizeCauseString(detailCauseRaw)
      let detailMessage =
        (detailObject.message as string | undefined) ||
        (detailObject.error as string | undefined) ||
        directMessage ||
        null
      let derivedDetails: ImportErrorDetail[] = []

      if (detailCauseValue && typeof detailCauseValue === 'object') {
        const detailCauseObject = detailCauseValue as Record<string, unknown>
        const nestedError = detailCauseObject.customErrorObject as
          | Record<string, unknown>
          | undefined
        derivedDetails = extractDetailsArray(nestedError?.details)
        if (!detailMessage) {
          detailMessage =
            (nestedError?.message as string | undefined) ||
            (detailCauseObject.message as string | undefined) ||
            detailMessage
        }
      }

      const response = {
        message: detailMessage,
        details: derivedDetails,
        rawCause: detailCauseRaw,
      }
      return response
    }

    const causeValue = normalizeCauseString(errorObject.Cause as string | undefined)

    if (causeValue && typeof causeValue === 'object') {
      const causeObject = causeValue as Record<string, unknown>
      let nestedError = causeObject.customErrorObject as Record<string, unknown> | undefined

      if (!nestedError && typeof causeObject.errorMessage === 'string') {
        const parsedNested = normalizeCauseString(causeObject.errorMessage)
        if (parsedNested && typeof parsedNested === 'object') {
          nestedError = parsedNested as Record<string, unknown>
        }
      }

      if (!nestedError && typeof causeObject.errorMessage === 'string') {
        try {
          const parsed = JSON.parse(causeObject.errorMessage)
          if (parsed && typeof parsed === 'object') {
            nestedError = (parsed as Record<string, unknown>).customErrorObject as
              | Record<string, unknown>
              | undefined
            if (!nestedError) {
              nestedError = parsed as Record<string, unknown>
            }
          }
        } catch (_error) {
          console.error('Failed to parse nested error JSON:', _error)
        }
      }

      const causeMessage =
        (nestedError?.message as string | undefined) ||
        (causeObject.message as string | undefined) ||
        directMessage ||
        null

      const details = extractDetailsArray(nestedError?.details) || []

      return {
        message: causeMessage,
        details,
        rawCause:
          nestedError && typeof causeObject.errorMessage === 'string'
            ? causeObject.errorMessage
            : typeof errorObject.Cause === 'string'
            ? (errorObject.Cause as string)
            : undefined,
      }
    }

    if (typeof causeValue === 'string') {
      return {
        message: causeValue,
        details: [],
        rawCause: causeValue,
      }
    }

    return {
      message: directMessage || null,
      details: extractDetailsArray(errorObject.details),
      rawCause: typeof errorObject.Cause === 'string' ? (errorObject.Cause as string) : undefined,
    }
  }

  return { message: String(error), details: [], rawCause: String(error) }
}

export const isImportFailed = (status?: ImportCollectionStatus): boolean => {
  return (
    status?.status === EAutoOnboardingStepStatus.FAILED ||
    status?.status === EAutoOnboardingStepStatus.CANCELLED
  )
}

export const hasImportDownload = (status?: ImportCollectionStatus): boolean => {
  return Boolean(status?.signedUrl && status.fileEmpty === false)
}

export const shouldEnableImportDetails = (status?: ImportCollectionStatus): boolean => {
  if (!status) return false
  if (isImportFailed(status)) return true
  return hasImportDownload(status)
}



// WEBPACK FOOTER //
// ./src/containers/Settings/Development/MigrateCompanyProducts/importStatusUtils.ts